import { ApiClient } from '@center-technologies/shared-api-clients';

export interface UnassignSubjectFromPerspectiveParams {
  perspectiveId?: string; // ID of the perspective to unassign from
  perspectiveSystemName?: string; // System name of the perspective (alternative to ID)
  sphereId?: string; // ID of the sphere (required if perspectiveSystemName is used)
  subjectId: string; // ID of the subject being unassigned
}

export interface UnassignSubjectFromPerspectiveResponse {
  success: boolean;
  message: string;
  error?: string; // Optional error field in case of failure
}

interface GetPerspectiveResponse {
  id: string;
}

export class UnassignSubjectError extends Error {
  details: string | string[];

  constructor(message: string, details: string | string[]) {
    super(message);
    this.details = details;
    this.name = 'UnassignSubjectError';
  }
}

/**
 * Helper function to look up a perspective ID using a sphere ID and perspective system name.
 *
 * @param apiClient - An ApiClient configured for your Graph endpoint
 * @param sphereId - ID of the sphere
 * @param perspectiveSystemName - System name of the perspective
 * @returns Promise resolving to the perspective ID
 */
async function getPerspectiveIdFromSystemName(
  apiClient: ApiClient,
  sphereId: string,
  perspectiveSystemName: string
): Promise<string> {
  try {
    const data = await apiClient.get<GetPerspectiveResponse>(
      `/spheres/${sphereId}/perspective/${perspectiveSystemName}`
    );
    return data.id;
  } catch (error: any) {
    console.error('Error fetching perspective ID:', error);
    throw new UnassignSubjectError(
      'Failed to fetch perspective ID from system name',
      error.response?.data?.error ?? error.message ?? 'Unknown error'
    );
  }
}

/**
 * Helper function to unassign a subject from a perspective by calling the API route.
 *
 * @param apiClient - An ApiClient configured for your Graph endpoint
 * @param params - Parameters for the API call
 * @returns Promise resolving to the API response
 * @throws UnassignSubjectError on failure
 */
export async function unassignSubjectFromPerspective(
  apiClient: ApiClient,
  params: UnassignSubjectFromPerspectiveParams
): Promise<UnassignSubjectFromPerspectiveResponse> {
  const { perspectiveId, perspectiveSystemName, sphereId, subjectId } = params;

  if (!subjectId?.trim()) {
    throw new UnassignSubjectError(
      'Invalid parameters',
      'subjectId is required'
    );
  }

  // Determine the perspective ID if perspectiveSystemName and sphereId are provided
  let resolvedPerspectiveId = perspectiveId;

  if (!resolvedPerspectiveId && perspectiveSystemName && sphereId) {
    try {
      resolvedPerspectiveId = await getPerspectiveIdFromSystemName(
        apiClient,
        sphereId,
        perspectiveSystemName
      );
    } catch (error) {
      throw new UnassignSubjectError(
        'Failed to resolve perspective ID from system name',
        (error as any).details ?? (error as any).message
      );
    }
  }

  if (!resolvedPerspectiveId) {
    throw new UnassignSubjectError(
      'Invalid parameters',
      'Either perspectiveId or both perspectiveSystemName and sphereId must be provided'
    );
  }

  try {
    // ApiClient.delete automatically handles the Bearer token header
    const data = await apiClient.delete<UnassignSubjectFromPerspectiveResponse>(
      `/perspectives/${resolvedPerspectiveId}/subjects/${subjectId}`
    );

    return data;
  } catch (error: any) {
    const msg =
      error.response?.data?.error ??
      error.message ??
      'Unknown error unassigning subject from perspective';
    
    console.error('Error unassigning subject from perspective:', msg);
    
    throw new UnassignSubjectError(
      'Failed to unassign subject from perspective',
      msg
    );
  }
}